import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { ShoppingBag, MapPin, Globe, ExternalLink } from 'lucide-react';
import { ProductCard } from './ProductCard';
import { Modal } from './Modal';
import { SchoolBadge } from './SchoolBadge';
import { normalizeProductSizes } from '../services/inventoryService';

export const ProductDetailModal: React.FC = () => {
  const { selectedProductDetail, closeProductDetail, products, schools, addToBasket } = useShop();

  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [quantityToReserve, setQuantityToReserve] = useState(1);

  if (!selectedProductDetail) return null;

  const product = selectedProductDetail;
  const school = schools.find((s) => s.id === product.schoolId);

  const availableSizes = normalizeProductSizes(product.sizes, product.size);

  // Auto-select first in-stock size if none selected or if product changed
  const activeSize = selectedSize || availableSizes.find((s) => s.stock > 0)?.size || availableSizes[0]?.size || 'Standard';
  const activeSizeObj = availableSizes.find((s) => s.size === activeSize);
  const activeStock = activeSizeObj ? activeSizeObj.stock : 0;

  const relatedProducts = products
    .filter((p) => p.id !== product.id && (p.schoolId === product.schoolId || p.categoryId === product.categoryId))
    .slice(0, 3);

  const handleAdd = () => {
    if (!activeSize) return;
    const safeQty = Math.min(activeStock, quantityToReserve);
    if (safeQty > 0) {
      addToBasket(product, activeSize, safeQty);
    }
    closeProductDetail();
  };

  return (
    <Modal
      isOpen={!!selectedProductDetail}
      onClose={closeProductDetail}
      title={product.name}
      subtitle={school?.name || 'Immingham School Uniform'}
      icon={<ShoppingBag className="w-5 h-5 text-[#002366]" />}
      maxWidth="4xl"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-[4/3] rounded-2xl bg-slate-100 overflow-hidden border border-slate-200/80 shadow-sm relative">
              <img
                src={product.images[selectedImageIdx] || product.images[0]}
                alt={product.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 bg-emerald-600 text-white font-bold text-xs px-3 py-1 rounded-full shadow-md">
                100% Free Donation
              </div>
            </div>

            {product.images.length > 1 && (
              <div className="flex items-center gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIdx(idx)}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition ${
                      selectedImageIdx === idx ? 'border-blue-600 ring-2 ring-blue-100' : 'border-slate-200 opacity-70'
                    }`}
                  >
                    <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info Column */}
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <SchoolBadge school={school} schoolName={school?.name} size="md" />
                {school?.website && (
                  <a
                    href={school.website.startsWith('http') ? school.website : `https://${school.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full border border-blue-200 transition"
                  >
                    <Globe className="w-3 h-3 text-blue-600" />
                    <span>Visit School Website</span>
                    <ExternalLink className="w-2.5 h-2.5 ml-0.5" />
                  </a>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
                {product.name}
              </h2>
              {product.color && (
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Colour: <strong className="text-slate-800">{product.color}</strong>
                </p>
              )}
            </div>

            {/* Gender & Attributes */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500 font-medium">Cut / Gender:</span>
              <strong className="text-slate-900 bg-slate-100 px-2.5 py-1 rounded-full">{product.gender || 'Unisex'}</strong>
            </div>

            {/* Available Sizes Selection */}
            <div className="space-y-2 p-4 rounded-2xl bg-slate-50 border border-slate-200/80">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">
                  Select Size *
                </span>
                <span className="text-slate-500 text-[11px]">
                  Selected: <strong className="text-blue-700">{activeSize}</strong> ({activeStock > 0 ? `${activeStock} in stock` : 'Out of stock'})
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {availableSizes.map((s) => {
                  const isSelected = activeSize === s.size;
                  const isInStock = s.stock > 0;
                  return (
                    <button
                      key={s.id || s.size}
                      type="button"
                      onClick={() => {
                        if (isInStock) {
                          setSelectedSize(s.size);
                          setQuantityToReserve(1);
                        }
                      }}
                      disabled={!isInStock}
                      className={`p-2.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50/90 ring-2 ring-blue-500/20 text-blue-950 font-bold shadow-sm'
                          : isInStock
                          ? 'border-slate-200 bg-white hover:border-slate-300 text-slate-800 font-medium'
                          : 'border-slate-200 bg-slate-100/70 text-slate-400 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <span className="text-xs font-bold block">{s.size}</span>
                      <span className={`text-[10px] font-semibold mt-1 block ${
                        s.stock > 2
                          ? 'text-emerald-700'
                          : s.stock > 0
                          ? 'text-amber-700 font-bold'
                          : 'text-rose-600 line-through'
                      }`}>
                        {s.stock > 2 ? `${s.stock} in stock` : s.stock > 0 ? `Low stock (${s.stock} left)` : '0 in stock'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Description &amp; Care
              </h4>
              <p className="text-xs text-slate-700 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Collection Note */}
            <div className="p-3.5 rounded-xl bg-sky-50 border border-sky-200/80 text-xs text-sky-950 flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
              <div>
                <strong className="block text-sky-900 font-bold">Bert Boyden Community Centre</strong>
                <span className="text-sky-800">{product.collectionNotes || 'Collect from Bert Boyden Community Centre during open hours.'}</span>
              </div>
            </div>

            {/* Quantity & Action */}
            <div className="pt-2 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600 font-medium">Size Status:</span>
                <span className={`font-bold ${activeStock > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {activeStock > 0 ? `${activeStock} Available in size ${activeSize}` : `Size ${activeSize} is Out of Stock`}
                </span>
              </div>

              {activeStock > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-slate-300 rounded-xl overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setQuantityToReserve(Math.max(1, quantityToReserve - 1))}
                      className="px-3 py-2 text-slate-600 hover:bg-slate-100 font-bold"
                    >
                      -
                    </button>
                    <span className="px-4 py-2 font-bold text-slate-900 text-sm">
                      {quantityToReserve}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantityToReserve(Math.min(activeStock, quantityToReserve + 1))}
                      className="px-3 py-2 text-slate-600 hover:bg-slate-100 font-bold"
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleAdd}
                    className="flex-1 py-3.5 px-6 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition active:scale-95"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    <span>Reserve Size {activeSize} ({quantityToReserve})</span>
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Related Items Section */}
        {relatedProducts.length > 0 && (
          <div className="pt-6 border-t border-slate-200">
            <h3 className="font-heading font-bold text-base sm:text-lg text-slate-900 mb-4">
              More Items from {school?.name || 'Immingham'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
